// ============================================================================
// HipocampoFlow — MS-09
// ----------------------------------------------------------------------------
// Diagrama SVG animado das 5 layers do Hipocampo OS:
//   Working → Short → Long
//                     ↑
//              People + Procedural alimentando
//
// Cada nó pulsa quando há atividade recente (controlada por pulseTokens).
// Cores via design tokens (HSL): primary, accent, secondary, success, warning.
// ============================================================================

import { useEffect, useState } from "react";
import type { HipocampoLayer, HipocampoLayerStat, HipocampoPulse } from "@/hooks/useHipocampoFlow";
import { cn } from "@/lib/utils";

interface NodeDef {
  key: HipocampoLayer;
  label: string;
  cx: number;
  cy: number;
  r: number;
  stroke: string; // tailwind text token (text-primary etc)
  fill: string;
}

const NODES: NodeDef[] = [
  { key: "working",    label: "Working",    cx:  90, cy: 150, r: 38, stroke: "stroke-primary",   fill: "fill-primary/15" },
  { key: "short",      label: "Short",      cx: 260, cy: 150, r: 42, stroke: "stroke-accent",    fill: "fill-accent/15" },
  { key: "long",       label: "Long",       cx: 450, cy: 150, r: 46, stroke: "stroke-success",   fill: "fill-success/15" },
  { key: "people",     label: "People",     cx: 175, cy:  40, r: 32, stroke: "stroke-warning",   fill: "fill-warning/15" },
  { key: "procedural", label: "Procedural", cx: 360, cy:  40, r: 32, stroke: "stroke-secondary-foreground", fill: "fill-secondary" },
];

const EDGES: Array<{ from: HipocampoLayer; to: HipocampoLayer }> = [
  { from: "working", to: "short" },
  { from: "short", to: "long" },
  { from: "people", to: "short" },
  { from: "procedural", to: "long" },
];

// Pulso visual: reseta sempre que `lastEventAt` muda (entrada nova chegou).
function usePulse(token: string | null): boolean {
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (!token) return;
    setActive(true);
    const t = setTimeout(() => setActive(false), 1800);
    return () => clearTimeout(t);
  }, [token]);
  return active;
}

interface NodeProps {
  node: NodeDef;
  stat: HipocampoLayerStat;
}

function FlowNode({ node, stat }: NodeProps) {
  const pulsing = usePulse(stat.lastEventAt);
  return (
    <g>
      {/* Anel de pulso (animado quando há atividade nova) */}
      {pulsing && (
        <circle
          cx={node.cx}
          cy={node.cy}
          r={node.r}
          className={cn("fill-none", node.stroke, "opacity-60")}
          style={{ animation: "hipocampoPulseRing 1.6s ease-out 1" }}
          strokeWidth={2}
        />
      )}
      <circle
        cx={node.cx}
        cy={node.cy}
        r={node.r}
        className={cn(node.fill, node.stroke, "transition-all duration-300")}
        strokeWidth={pulsing ? 2.5 : 1.5}
      />
      <text
        x={node.cx}
        y={node.cy - 4}
        textAnchor="middle"
        className="fill-foreground text-[11px] font-semibold"
      >
        {node.label}
      </text>
      <text
        x={node.cx}
        y={node.cy + 12}
        textAnchor="middle"
        className="fill-foreground text-[14px] font-bold tabular-nums"
      >
        {stat.total}
      </text>
      <text
        x={node.cx}
        y={node.cy + 26}
        textAnchor="middle"
        className="fill-muted-foreground text-[9px] tabular-nums"
      >
        +{stat.last_24h} / 24h
      </text>
    </g>
  );
}

interface EdgeProps {
  from: NodeDef;
  to: NodeDef;
  active: boolean;
}

function FlowEdge({ from, to, active }: EdgeProps) {
  return (
    <line
      x1={from.cx}
      y1={from.cy}
      x2={to.cx}
      y2={to.cy}
      className={cn(
        "stroke-border transition-opacity duration-500",
        active && "stroke-primary",
      )}
      strokeWidth={active ? 2 : 1}
      strokeDasharray={active ? "4 4" : undefined}
      style={active ? { animation: "hipocampoDash 1s linear infinite" } : undefined}
    />
  );
}

interface HipocampoFlowProps {
  layers: Record<HipocampoLayer, HipocampoLayerStat>;
  pulses: HipocampoPulse[];
}

export function HipocampoFlow({ layers, pulses }: HipocampoFlowProps) {
  // Edge "ativa" = um dos nós ligados pulsou nos últimos 30s.
  const recentLayers = new Set<HipocampoLayer>();
  const cutoff = Date.now() - 30_000;
  for (const p of pulses) {
    if (new Date(p.at).getTime() >= cutoff) recentLayers.add(p.layer);
  }

  const nodeMap = new Map(NODES.map((n) => [n.key, n]));

  return (
    <div className="w-full overflow-x-auto rounded-lg border bg-card p-2">
      {/* Keyframes locais para o pulso e dash dos edges. */}
      <style>{`
        @keyframes hipocampoPulseRing {
          0%   { transform-origin: center; transform: scale(1);   opacity: 0.7; }
          100% { transform-origin: center; transform: scale(1.6); opacity: 0;   }
        }
        @keyframes hipocampoDash {
          to { stroke-dashoffset: -16; }
        }
      `}</style>
      <svg viewBox="0 0 540 220" className="w-full h-[220px]" role="img" aria-label="Hipocampo OS — fluxo das 5 layers">
        <g>
          {EDGES.map((e) => {
            const a = nodeMap.get(e.from);
            const b = nodeMap.get(e.to);
            if (!a || !b) return null;
            const active = recentLayers.has(e.from) || recentLayers.has(e.to);
            return <FlowEdge key={`${e.from}-${e.to}`} from={a} to={b} active={active} />;
          })}
        </g>
        <g>
          {NODES.map((n) => (
            <FlowNode key={n.key} node={n} stat={layers[n.key]} />
          ))}
        </g>
      </svg>
    </div>
  );
}
